// client/src/components/pages/LexiStar/Messages/Messages.jsx
//
// LexiStar message log (ST-00188 M3, T8-S08/S09; FR-NTF-008/009, FR-DRV-005).
// Route /lexistar/messages, page permission lexistar.messages.view.
// Data: GET /messages, /messages/:id, /cost-ledger, /delivery-slo
// (m3-contracts §7.2).
//
//  - Header: spend this month per channel and delivery over the last 24 h
//    (sent / delivered / failed / p95 time to deliver).
//  - Delivery log: every message in and out with its state chip
//    (queued · sent · delivered · read · failed · suppressed · received) and
//    cost; filters for channel, state, direction, driver, trip and dates, all
//    in the URL. A row opens the full message with its fallback chain.
//  - With a driver chosen, the conversation view shows the thread as bubbles.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaComments } from 'react-icons/fa';
import UnifiedDateRangePicker from '../../../common/UnifiedDateRangePicker';
import ThemedSelect from '../../../common/ThemedSelect';
import SegmentedControl from '../../../common/SegmentedControl';
import { useAuth } from '../../../../context/AuthContext';
import {
  PageHeader,
  ErrorBanner,
  NoPermissionPage,
  LoadingState,
  EmptyState,
  inputClasses,
  cardClasses,
} from '../StatusBoard/atoms';
import { fmtDateTime, fmtDuration } from '../StatusBoard/format';
import {
  listMessages,
  getCostLedger,
  getDeliverySlo,
  pickRows,
} from '../NotificationRules/notificationsApi';
import { listDrivers } from '../Drivers/driversApi';
import { StateChip, ChannelBadge } from './MessageAtoms';
import Conversation from './Conversation';
import MessageDetail from './MessageDetail';
import {
  CHANNELS,
  MESSAGE_STATES,
  stateMeta,
  channelLabel,
  intentLabel,
  recipientText,
  fmtZar,
  messageCost,
} from './messageMeta';

const PAGE = 100;
const DAY = 86400000;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const dayStart = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseYmd = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

function Stat({ label, value, sub, tone }) {
  return (
    <div className={`${cardClasses} px-4 py-3 min-w-0`}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 truncate">
        {label}
      </p>
      <p className={`text-xl font-bold tabular-nums ${tone || 'text-slate-900'}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 truncate">{sub}</p>}
    </div>
  );
}

const Messages = () => {
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.messages.view');
  const [params, setParams] = useSearchParams();
  const channels = (params.get('channel') || '').split(',').filter(Boolean);
  const states = (params.get('state') || '').split(',').filter(Boolean);
  const direction = params.get('direction') || '';
  const driverId = params.get('driver_id') || '';
  const tripId = params.get('trip_id') || '';
  const from = parseYmd(params.get('from')) || new Date(dayStart(Date.now()).getTime() - 6 * DAY);
  const to = parseYmd(params.get('to')) || dayStart(Date.now());
  const view = driverId && params.get('view') !== 'log' ? 'conversation' : 'log';

  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [spend, setSpend] = useState([]);
  const [slo, setSlo] = useState([]);
  const [open, setOpen] = useState(null);
  const [tripInput, setTripInput] = useState(tripId);

  const setParam = useCallback(
    (patch) => {
      const next = new URLSearchParams(params);
      Object.entries(patch).forEach(([k, v]) => {
        const val = Array.isArray(v) ? v.join(',') : v;
        if (val === null || val === undefined || val === '') next.delete(k);
        else next.set(k, val);
      });
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const query = useMemo(
    () => ({
      channel: channels.join(','),
      state: states.join(','),
      direction,
      driver_id: driverId,
      trip_id: UUID_RE.test(tripId) ? tripId : '',
      from: dayStart(from).toISOString(),
      to: new Date(dayStart(to).getTime() + DAY).toISOString(),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params]
  );

  const load = useCallback(
    async (p = 1) => {
      if (!canView) return;
      setLoading(true);
      try {
        const res = await listMessages({ ...query, page: p, limit: PAGE });
        const rows = pickRows(res, 'messages');
        setMessages((prev) => (p === 1 ? rows : [...prev, ...rows]));
        setTotal(res?.pagination?.total ?? res?.total ?? null);
        setPage(p);
        setError(null);
      } catch (err) {
        setError(
          err.response?.status === 503
            ? 'The message log is not installed on this server yet.'
            : err.response?.data?.error || 'Failed to load messages.'
        );
      } finally {
        setLoading(false);
      }
    },
    [canView, query]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  useEffect(() => {
    if (!canView) return;
    listDrivers({ limit: 500 })
      .then((res) => setDrivers(pickRows(res, 'drivers')))
      .catch(() => setDrivers([]));
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    getCostLedger({ month, limit: 1 })
      .then((res) => setSpend(pickRows(res, 'totals', 'spend')))
      .catch(() => setSpend([]));
    getDeliverySlo({ from: new Date(Date.now() - DAY).toISOString(), to: now.toISOString() })
      .then((res) => setSlo(pickRows(res, 'totals')))
      .catch(() => setSlo([]));
  }, [canView]);

  const spendByChannel = useMemo(() => {
    const m = {};
    spend.forEach((r) => (m[r.channel] = (m[r.channel] || 0) + (Number(r.amount_zar) || 0)));
    return m;
  }, [spend]);
  const sloTotals = useMemo(
    () =>
      slo.reduce(
        (a, r) => ({
          sent: a.sent + (Number(r.sent) || 0),
          delivered: a.delivered + (Number(r.delivered) || 0),
          failed: a.failed + (Number(r.failed) || 0),
          p95: Math.max(a.p95, Number(r.worst_p95_s ?? r.p95_s) || 0),
        }),
        { sent: 0, delivered: 0, failed: 0, p95: 0 }
      ),
    [slo]
  );
  const driver = drivers.find((d) => d.id === driverId);

  const header = (
    <PageHeader
      icon={FaComments}
      title="Messages"
      subtitle="Every message to and from drivers and controllers, with its delivery and cost."
      toolbar={
        canView && driverId ? (
          <SegmentedControl
            options={[
              { value: 'conversation', label: 'Conversation' },
              { value: 'log', label: 'Log' },
            ]}
            value={view}
            onChange={(v) => setParam({ view: v === 'log' ? 'log' : null })}
            ariaLabel="Messages view"
          />
        ) : null
      }
    />
  );

  if (!canView) {
    return (
      <NoPermissionPage
        header={header}
        message="You do not have permission to view the message log."
      />
    );
  }

  const totalSpend = Object.values(spendByChannel).reduce((a, b) => a + b, 0);

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-5 space-y-4">
        <ErrorBanner message={error} onDismiss={() => setError(null)} />

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" aria-label="Delivery and spend">
          <Stat
            label="Spend this month"
            value={fmtZar(totalSpend)}
            sub={
              ['whatsapp', 'sms', 'telegram']
                .filter((c) => spendByChannel[c])
                .map((c) => `${channelLabel(c)} ${fmtZar(spendByChannel[c])}`)
                .join(' · ') || 'nothing billed yet'
            }
          />
          <Stat label="Sent (24 h)" value={sloTotals.sent} />
          <Stat
            label="Delivered (24 h)"
            value={sloTotals.delivered}
            sub={
              sloTotals.sent
                ? `${Math.round((sloTotals.delivered / sloTotals.sent) * 100)}% of sent`
                : null
            }
          />
          <Stat
            label="Failed (24 h)"
            value={sloTotals.failed}
            tone={sloTotals.failed ? 'text-red-700' : 'text-slate-900'}
          />
          <Stat
            label="Slowest p95"
            value={sloTotals.p95 ? fmtDuration(sloTotals.p95) : '—'}
            sub="time to deliver"
          />
        </div>

        <section className={`${cardClasses} p-4`} aria-label="Filters" role="search">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-3">
            <div className="xl:col-span-1">
              <ThemedSelect
                aria-label="Channel"
                isMulti
                value={channels}
                onChange={(e) => setParam({ channel: e.target.value })}
                className={inputClasses}
                placeholder="All channels"
              >
                {CHANNELS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </ThemedSelect>
            </div>
            <div className="xl:col-span-1">
              <ThemedSelect
                aria-label="State"
                isMulti
                value={states}
                onChange={(e) => setParam({ state: e.target.value })}
                className={inputClasses}
                placeholder="All states"
              >
                {MESSAGE_STATES.map((s) => (
                  <option key={s} value={s}>
                    {stateMeta(s).label}
                  </option>
                ))}
              </ThemedSelect>
            </div>
            <div className="xl:col-span-1">
              <ThemedSelect
                aria-label="Direction"
                value={direction}
                onChange={(e) => setParam({ direction: e.target.value })}
                className={inputClasses}
                isClearable
              >
                <option value="">In and out</option>
                <option value="out">Sent</option>
                <option value="in">Received</option>
              </ThemedSelect>
            </div>
            <div className="xl:col-span-1">
              <ThemedSelect
                aria-label="Driver"
                value={driverId}
                onChange={(e) => setParam({ driver_id: e.target.value, view: null })}
                className={inputClasses}
                isClearable
              >
                <option value="">All drivers</option>
                {drivers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </ThemedSelect>
            </div>
            <div className="xl:col-span-1">
              <label htmlFor="msg-trip" className="sr-only">
                Trip id
              </label>
              <input
                id="msg-trip"
                value={tripInput}
                onChange={(e) => setTripInput(e.target.value)}
                onBlur={() => setParam({ trip_id: tripInput.trim() })}
                onKeyDown={(e) => e.key === 'Enter' && setParam({ trip_id: tripInput.trim() })}
                placeholder="Trip id"
                className={`${inputClasses} font-mono text-xs`}
              />
            </div>
            <div className="xl:col-span-1">
              <UnifiedDateRangePicker
                startDate={from}
                endDate={to}
                onStartDateChange={(d) => d && setParam({ from: ymd(d) })}
                onEndDateChange={(d) => d && setParam({ to: ymd(d) })}
              />
            </div>
          </div>
        </section>

        <section
          className={cardClasses}
          aria-label={view === 'conversation' ? 'Conversation' : 'Delivery log'}
        >
          {view === 'conversation' ? (
            <>
              <div className="px-4 py-3 border-b border-slate-200">
                <h2 className="text-sm font-semibold text-slate-900">{driver?.name || 'Driver'}</h2>
                {driver?.phone_e164 && (
                  <p className="text-xs text-slate-500 tabular-nums">{driver.phone_e164}</p>
                )}
              </div>
              {loading && !messages.length ? (
                <LoadingState label="Loading the conversation..." />
              ) : (
                <Conversation
                  messages={messages}
                  driverName={driver?.name}
                  onOpenMessage={setOpen}
                />
              )}
            </>
          ) : loading && !messages.length ? (
            <LoadingState label="Loading messages..." />
          ) : messages.length === 0 ? (
            <EmptyState icon={FaComments} title="No messages">
              Nothing matches these filters. Messages appear here as rules fire and drivers reply.
            </EmptyState>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" aria-label="Messages">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs text-slate-500">
                    <th className="px-4 py-2 font-medium">When</th>
                    <th className="px-2 py-2 font-medium">
                      <span className="sr-only">Direction</span>
                    </th>
                    <th className="px-4 py-2 font-medium">Channel</th>
                    <th className="px-4 py-2 font-medium">Recipient</th>
                    <th className="px-4 py-2 font-medium">Purpose</th>
                    <th className="px-4 py-2 font-medium">State</th>
                    <th className="px-4 py-2 font-medium text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.map((m) => {
                    const cost = messageCost(m);
                    return (
                      <tr
                        key={m.id}
                        onClick={() => setOpen(m)}
                        className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                        aria-label={`${m.direction === 'in' ? 'Received' : 'Sent'} ${channelLabel(m.channel)} ${intentLabel(m)}`}
                      >
                        <td className="px-4 py-2 whitespace-nowrap tabular-nums text-slate-700">
                          {fmtDateTime(m.created_at)}
                        </td>
                        <td
                          className="px-2 py-2 text-slate-500"
                          title={m.direction === 'in' ? 'Received' : 'Sent'}
                        >
                          {m.direction === 'in' ? '↙' : '↗'}
                        </td>
                        <td className="px-4 py-2">
                          <ChannelBadge channel={m.channel} />
                          {m.fallback_of && (
                            <span className="block text-[10px] text-amber-700">fallback</span>
                          )}
                        </td>
                        <td className="px-4 py-2 max-w-[16rem] truncate">{recipientText(m)}</td>
                        <td className="px-4 py-2">
                          {intentLabel(m)}
                          {m.body && (
                            <span className="block text-xs text-slate-500 truncate max-w-[22rem]">
                              {m.body}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <StateChip state={m.state} />
                          {m.state === 'failed' && m.error && (
                            <span className="block text-[11px] text-red-700 truncate max-w-[14rem]">
                              {m.error}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {cost !== null ? fmtZar(cost) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {messages.length > 0 &&
            (total === null ? messages.length >= page * PAGE : messages.length < total) && (
              <div className="px-4 py-3 border-t border-slate-100 text-center">
                <button
                  type="button"
                  onClick={() => load(page + 1)}
                  disabled={loading}
                  className="px-4 py-1.5 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  {loading
                    ? 'Loading...'
                    : `Load more${total !== null ? ` (${messages.length} of ${total})` : ''}`}
                </button>
              </div>
            )}
        </section>
      </div>
      {open && <MessageDetail message={open} onClose={() => setOpen(null)} />}
    </div>
  );
};

export default Messages;
